import { Route, Switch } from 'react-router-dom';
import { Role } from '../../common/constants';
import ErrorBoundary from '../../components/errorBoundary';
import ErrorCode from '../errorCode';
import ProtectedRoute from '../../components/protectedRoute';
import Login from '../login';
import MfaVerify from '../mfa';
import RequestMfaReset from '../requestMfaReset';
import ConfirmMfaReset from '../confirmMfaReset';
import Admin from '../admin';
import ApproveCaseload from '../caseload/approve';
import ApproveTherapist from '../admin/therapists/approve';
import BillCreate from '../quickbooks';
import BillExportDetails from '../quickbooks';
import BillExports from '../quickbooks';
import Caseloads from '../caseload';
import CaseloadsTransferStep2 from '../caseloadsTransfer/transfer';
import CaseloadTransfer from '../caseloadsTransfer';
import ChangePassword from '../changePassword';
import Dashboard from '../dashboard';
import DistrictCalendar from '../calendar/readonly';
import Districts from '../admin/districts';
import EditTherapist from '../admin/therapists/edit';
import ForcedChangePassword from '../forcedChangePassword';
import ForgotPassword from '../forgotPassword';
import Healthcheck from '../healthcheck';
import Invoice from '../invoice';
import ManageNotification from '../admin/notification';
import Messages from '../messages';
import MyProfile from '../myProfile';
import Programs from '../admin/programs';
import Proofing from '../proofing';
import ProofingDetail from '../proofing/detail';
import TimesheetSearch from '../admin/timesheetSearch';
import Rates from '../admin/rates';
import Reports from '../reports/index';
import ResetPassword from '../resetPassword';
import Schools from '../admin/schools';
import SearchTherapists from '../admin/therapists/SearchTherapists';
import Timesheets from '../timesheets';
import CaseloadSearch from '../caseload/caseloadSearch';
import ProofingSummary from '../proofing/summary';
import Users from '../admin/users';
import ProofingSummarySearch from '../proofing/summarySearch';
import TimeEntryTransfer from '../proofing/transfer';
import ApproveRates from '../admin/rates/approve';
import NewNotificationPage from '../admin/notification/new';
import EditNotificationPage from '../admin/notification/edit';
import Duplicates from '../caseload/duplicates';
import { useApplyLocalStorage } from '../../app/hooks/useApplyLocalStorage';
import HomeDistricts from '../admin/homeDistricts';
import ViewHomeDistricts from '../admin/homeDistricts/view';
import UnlockTimesheets from '../admin/therapists/unlockTimesheets';
import UserSessionTimeout from '../../common/userSessionTimeout';
import ManualMerge from '../caseload/manualMerge';

const App = () => {
  useApplyLocalStorage();

  return (
    <ErrorBoundary>
      <UserSessionTimeout />
      <Switch>
        <Route exact path='/login' component={Login} />
        <Route exact path='/mfa' component={MfaVerify} />
        <Route exact path='/request-mfa-reset' component={RequestMfaReset} />
        <Route exact path='/confirm-mfa-reset' component={ConfirmMfaReset} />
        <Route exact path='/healthcheck' component={Healthcheck} />
        <Route exact path='/forgot-password' component={ForgotPassword} />
        <Route exact path='/reset-password' component={ResetPassword} />
        <ProtectedRoute
          exact
          path='/admin'
          component={Dashboard}
          roles={[Role.Admin, Role.Director, Role.BillingSpecialist, Role.Operations]}
        />
        <ProtectedRoute
          exact
          path='/admin/therapists'
          component={SearchTherapists}
          roles={[Role.Admin, Role.Director, Role.BillingSpecialist, Role.Accounting, Role.Operations]}
        />
        <ProtectedRoute
          exact
          path='/admin/therapists/approve'
          component={ApproveTherapist}
          roles={[Role.Director, Role.BillingSpecialist]}
        />
        <ProtectedRoute
          exact
          path='/admin/therapists/unlockTimesheets'
          component={UnlockTimesheets}
          roles={[Role.Director, Role.BillingSpecialist]}
        />
        <ProtectedRoute
          exact
          path='/admin/therapists/:id'
          component={EditTherapist}
          roles={[Role.Admin, Role.Director, Role.BillingSpecialist, Role.Accounting, Role.Operations]}
        />
        <ProtectedRoute
          exact
          path='/admin/notifications'
          component={ManageNotification}
          roles={[Role.Admin, Role.BillingSpecialist]}
        />
        <ProtectedRoute
          exact
          path='/admin/notifications/new'
          component={NewNotificationPage}
          roles={[Role.Admin, Role.BillingSpecialist]}
        />
        <ProtectedRoute
          exact
          path='/admin/notifications/:id'
          component={EditNotificationPage}
          roles={[Role.Admin, Role.BillingSpecialist]}
        />
        <ProtectedRoute
          exact
          path='/admin/districts'
          component={Districts}
          roles={[Role.Admin, Role.Director, Role.BillingSpecialist, Role.Accounting, Role.Operations]}
        />
        <ProtectedRoute
          exact
          path='/admin/districts/rates/approve'
          component={ApproveRates}
          roles={[Role.Admin, Role.Director, Role.BillingSpecialist, Role.Operations]}
        />
        <ProtectedRoute
          exact
          path='/admin/districts/:id'
          component={Districts}
          roles={[Role.Admin, Role.Director, Role.BillingSpecialist, Role.Accounting, Role.Operations]}
        />
        <ProtectedRoute
          exact
          path='/admin/districts/:id/calendar'
          component={DistrictCalendar}
          roles={[Role.Admin, Role.Director, Role.BillingSpecialist, Role.Therapist]}
        />
        <ProtectedRoute
          exact
          path='/admin/districts/:districtId/schools/*'
          component={Schools}
          roles={[Role.Admin, Role.Director, Role.BillingSpecialist]}
        />
        <ProtectedRoute
          exact
          path='/admin/districts/:districtId/programs/*'
          component={Programs}
          roles={[Role.Admin, Role.Director, Role.BillingSpecialist]}
        />
        <ProtectedRoute
          exact
          path='/admin/districts/:districtId/rates/*'
          component={Rates}
          roles={[Role.Admin, Role.Director, Role.BillingSpecialist, Role.Operations]}
        />
        <ProtectedRoute
          exact
          path='/admin/homeDistricts'
          component={HomeDistricts}
          roles={[Role.Admin, Role.BillingSpecialist, Role.Operations]}
        />
        <ProtectedRoute
          exact
          path='/admin/homeDistricts/new'
          component={HomeDistricts}
          roles={[Role.Admin, Role.BillingSpecialist, Role.Operations]}
        />
        <ProtectedRoute
          exact
          path='/admin/homeDistricts/:id'
          component={HomeDistricts}
          roles={[Role.Admin, Role.BillingSpecialist, Role.Operations]}
        />
        <ProtectedRoute
          exact
          path='/admin/users'
          component={Users}
          roles={[Role.Admin, Role.Director, Role.BillingSpecialist, Role.Operations]}
        />
        <ProtectedRoute
          exact
          path='/admin/users/*'
          component={Users}
          roles={[Role.Admin, Role.Director, Role.BillingSpecialist, Role.Operations]}
        />
        <ProtectedRoute
          exact
          path='/admin/*'
          component={Admin}
          roles={[Role.Admin, Role.BillingSpecialist, Role.Director, Role.Accounting]}
        />
        <ProtectedRoute exact path='/' component={Dashboard} />
        <ProtectedRoute path='/my-profile' component={MyProfile} />
        <ProtectedRoute path='/change-password' component={ChangePassword} />
        <ProtectedRoute
          path='/forced-change-password'
          component={ForcedChangePassword}
        />
        <ProtectedRoute path='/messages/' component={Messages} />
        <ProtectedRoute
          exact
          path='/timesheets'
          component={Timesheets}
          roles={[Role.Therapist, Role.Director, Role.BillingSpecialist, Role.Accounting]}
          exceptRoles={[Role.SuperUser]}
        />
        <ProtectedRoute
          exact
          path='/proofing'
          component={Proofing}
          roles={[Role.Admin, Role.BillingSpecialist]}
        />
        <ProtectedRoute
          exact
          path='/accounting/invoice'
          component={Invoice}
          roles={[Role.Admin, Role.Director, Role.BillingSpecialist]}
        />
        <ProtectedRoute
          exact
          path='/accounting/invoice/*'
          component={Invoice}
          roles={[Role.Admin, Role.Director, Role.BillingSpecialist, Role.Accounting]}
        />
        <ProtectedRoute
          exact
          path='/accounting/invoice/exports'
          component={Invoice}
          roles={[Role.Admin, Role.Director, Role.BillingSpecialist, Role.Accounting]}
        />
        <ProtectedRoute
          exact
          path='/accounting/bill'
          component={BillCreate}
          roles={[Role.Admin, Role.BillingSpecialist, Role.Accounting]}
        />
        <ProtectedRoute
          exact
          path='/accounting/bill/exports'
          component={BillExports}
          roles={[Role.Admin, Role.Director, Role.BillingSpecialist, Role.Accounting]}
        />
        <ProtectedRoute
          exact
          path='/accounting/bill/exports/details/:id'
          component={BillExportDetails}
          roles={[Role.Admin, Role.Director, Role.BillingSpecialist, Role.Accounting]}
        />
        <ProtectedRoute
          exact
          path='/proofing/week/:id'
          component={ProofingDetail}
          roles={[Role.Director, Role.BillingSpecialist]}
        />
        <ProtectedRoute
          exact
          path='/proofing/summary'
          component={ProofingSummarySearch}
          roles={[Role.BillingSpecialist]}
        />
        <ProtectedRoute
          exact
          path='/proofing/transfer'
          component={TimeEntryTransfer}
          roles={[Role.BillingSpecialist]}
        />
        <ProtectedRoute
          exact
          path='/proofing/summary/:id'
          component={ProofingSummary}
          roles={[Role.Admin, Role.BillingSpecialist]}
        />
        <ProtectedRoute
          path='/caseload/'
          component={Caseloads}
          roles={[Role.Admin, Role.BillingSpecialist, Role.Therapist, Role.Director]}
          exact
          exceptRoles={[Role.SuperUser]}
        />
        <ProtectedRoute
          path='/caseload/'
          component={Caseloads}
          roles={[Role.Admin, Role.BillingSpecialist, Role.Therapist, Role.Director]}
        />
        <ProtectedRoute
          exact
          path='/caseload/duplicates'
          component={Duplicates}
          roles={[Role.Admin, Role.BillingSpecialist]}
        />
        <ProtectedRoute
          path='/caseload/approve'
          component={ApproveCaseload}
          roles={[Role.BillingSpecialist]}
        />
        <ProtectedRoute
          path='/caseloadTransfer/transfer'
          component={CaseloadsTransferStep2}
          roles={[Role.BillingSpecialist]}
        />
        <ProtectedRoute
          path='/caseloadTransfer/'
          component={CaseloadTransfer}
          roles={[Role.BillingSpecialist]}
        />
        <ProtectedRoute
          path='/caseload/searchCaseloads'
          component={CaseloadSearch}
          roles={[Role.Admin, Role.BillingSpecialist, Role.Director]}
        />
        <ProtectedRoute
          path='/caseload/manualmerge'
          component={ManualMerge}
          roles={[Role.Admin, Role.BillingSpecialist, Role.Director]}
        />
        <ProtectedRoute
          exact
          path='/reports/*'
          component={Reports}
          roles={[Role.Admin, Role.BillingSpecialist, Role.Operations, Role.Accounting]}
        />
        <ProtectedRoute
          exact
          path='/admin/timesheetSearch'
          component={TimesheetSearch}
          roles={[Role.Director, Role.Admin, Role.BillingSpecialist, Role.Accounting]}
        />
        <Route>
          <ErrorCode
            code='404'
            message="Sorry, we can't find the page you're looking for."
            to='/'
          />
        </Route>
      </Switch>
    </ErrorBoundary>
  );
}
export default App;